/**
 * Optimizer Service
 * API client for the MIP Optimizer endpoints.
 */
import { API_BASE_URL } from '../config';
import type {
    OptimizerConfig,
    FlightInput,
    MIPOptimizerResult,
    OptimizerTestResponse,
    OptimizerStatusResponse,
    OptimizerRunRequest,
    CandidatesResponse,
    SlotCatalogResponse,
    SlotCatalogRequest,
    AutoAssignRequest,
    AutoAssignResponse,
    PendingFlight,
    DEFAULT_OPTIMIZER_CONFIG
} from '../types/optimizer';

const OPTIMIZER_BASE = `${API_BASE_URL}/optimizer`;

/**
 * Get current access token from localStorage.
 */
function getAuthToken(): string | null {
    return localStorage.getItem('access_token');
}

/**
 * Create headers with authentication.
 */
function getHeaders(): HeadersInit {
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };

    const token = getAuthToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Check optimizer status and OR-Tools availability.
 */
export async function getOptimizerStatus(): Promise<OptimizerStatusResponse> {
    const response = await fetch(`${OPTIMIZER_BASE}/status`, {
        method: 'GET',
        headers: getHeaders(),
    });

    if (!response.ok) {
        throw new Error(`Failed to get optimizer status: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Run optimizer test with synthetic data.
 */
export async function testOptimizer(params?: {
    num_flights?: number;
    num_stands?: number;
    min_turnaround?: number;
    lambda_weight?: number;
}): Promise<OptimizerTestResponse> {
    const queryParams = new URLSearchParams();

    if (params?.num_flights) queryParams.set('num_flights', params.num_flights.toString());
    if (params?.num_stands) queryParams.set('num_stands', params.num_stands.toString());
    if (params?.min_turnaround) queryParams.set('min_turnaround', params.min_turnaround.toString());
    if (params?.lambda_weight) queryParams.set('lambda_weight', params.lambda_weight.toString());

    const url = `${OPTIMIZER_BASE}/test${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Test failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Run optimizer with provided flights.
 */
export async function runOptimizer(
    flights: FlightInput[],
    config?: Partial<OptimizerConfig>
): Promise<MIPOptimizerResult> {
    const request: OptimizerRunRequest = {
        config: {
            capacity_limit: config?.capacity_limit ?? 30.0,
            min_turnaround: config?.min_turnaround ?? 45,
            lambda_weight: config?.lambda_weight ?? 0.5,
            max_slots: config?.max_slots ?? 20,
            day_start: config?.day_start ?? 360,
            day_end: config?.day_end ?? 1380,
            time_limit_seconds: config?.time_limit_seconds ?? 30.0,
        },
        flights,
    };

    const response = await fetch(`${OPTIMIZER_BASE}/run`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Optimizer failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Run optimizer for a specific project.
 */
export async function runOptimizerForProject(
    projectId: string,
    config?: Partial<OptimizerConfig>
): Promise<MIPOptimizerResult> {
    const response = await fetch(`${OPTIMIZER_BASE}/run/${projectId}`, {
        method: 'POST',
        headers: getHeaders(),
        body: config ? JSON.stringify(config) : undefined,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Optimizer failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Get slot candidates for a project (without running full optimization).
 */
export async function getSlotCandidates(
    projectId: string,
    minGap: number = 30
): Promise<CandidatesResponse> {
    const response = await fetch(
        `${OPTIMIZER_BASE}/candidates/${projectId}?min_gap=${minGap}`,
        {
            method: 'GET',
            headers: getHeaders(),
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to get candidates: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Generate enriched slot catalog from flights.
 */
export async function getSlotCatalog(
    flights: FlightInput[],
    config: OptimizerConfig,
    baseRevenueRate: number = 150.0
): Promise<SlotCatalogResponse> {
    const request: SlotCatalogRequest = {
        flights,
        config,
        base_revenue_rate: baseRevenueRate,
    };

    const response = await fetch(`${OPTIMIZER_BASE}/slot-catalog`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Slot catalog generation failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Automatically assign pending flights to available slots.
 */
export async function autoAssignFlights(
    flights: FlightInput[],
    pendingFlights: PendingFlight[],
    config: OptimizerConfig,
    baseRevenueRate: number = 150.0,
    respectSegment: boolean = true
): Promise<AutoAssignResponse> {
    const request: AutoAssignRequest = {
        flights,
        pending_flights: pendingFlights,
        config,
        base_revenue_rate: baseRevenueRate,
        respect_segment: respectSegment,
    };

    const response = await fetch(`${OPTIMIZER_BASE}/auto-assign`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Auto-assignment failed: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Enqueue the Part-5 optimizer for a real project as a Celery job. The heavy MIP
 * runs on a worker; poll getProjectTask(task_id) for the result. Returns 409 with
 * a clear message if the project has no per-flight stand allocation to optimise.
 */
export async function runOptimizerForProjectAsync(
    projectId: string,
    config: Partial<OptimizerConfig> = {}
): Promise<{ task_id: string; status: string; project_id: string }> {
    const response = await fetch(`${OPTIMIZER_BASE}/project/${projectId}/run-async`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(config),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        // Attach the HTTP status so callers can branch on 409 (no stand allocation)
        // instead of string-matching the message text.
        const err = new Error(error.detail || `Optimizer enqueue failed: ${response.statusText}`) as Error & { status?: number };
        err.status = response.status;
        throw err;
    }
    return response.json();
}

/**
 * Enqueue the CANONICAL to-be (FTS bridge decision #2) as a Celery job: re-runs
 * the one DACAMS pipeline over an FTS datasheet of improved timestamps and
 * persists tobe_controlling_capacity onto the project, which the optimizer then
 * uses via capacity_source='to_be'. Poll with getProjectOptimizerTask(task_id).
 * config: { closure_factor (0-1, default 0.5), delay89_reduced_min (default 0) }.
 */
export async function computeCanonicalTobeAsync(
    projectId: string,
    config: { closure_factor?: number; delay89_reduced_min?: number } = {}
): Promise<{ task_id: string; status: string; project_id: string }> {
    const response = await fetch(`${OPTIMIZER_BASE}/project/${projectId}/compute-tobe-async`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(config),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `To-be enqueue failed: ${response.statusText}`);
    }
    return response.json();
}

/** Poll an optimizer Celery job by task_id. */
export async function getProjectOptimizerTask(taskId: string): Promise<{
    task_id: string;
    state: string;
    progress?: number;
    result?: MIPOptimizerResult;
    error?: string;
}> {
    const response = await fetch(`${OPTIMIZER_BASE}/task/${taskId}`, { headers: getHeaders() });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Optimizer poll failed: ${response.statusText}`);
    }
    return response.json();
}

/**
 * Optimizer service object for convenience.
 */
export const optimizerService = {
    getStatus: getOptimizerStatus,
    test: testOptimizer,
    run: runOptimizer,
    runForProject: runOptimizerForProject,
    runForProjectAsync: runOptimizerForProjectAsync,
    computeCanonicalTobeAsync,
    getProjectTask: getProjectOptimizerTask,
    getCandidates: getSlotCandidates,
    getSlotCatalog,
    autoAssign: autoAssignFlights,
};

export default optimizerService;

